import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { MultiSelect } from 'primereact/multiselect';
import {
    useGetAnalysisTypesQuery,
    useGetSessionAnalysisTypesQuery,
    useSaveAnalysisTypesMutation,
    useSaveAnalysisNotesMutation,
    useGetCapDailyMetricSummaryQuery,
    // useGetSessionByIdQuery,
    useCalculateNextBestActionAssociateMutation,
} from '../coachingActionPlanSlice';
import { CoachingHistory } from '../coachingHistory/CoachingHistory';
import { MetricMonthlySummaryCard } from '../../../components/metricMonthlySummaryCard/MetricMonthlySummaryCard';
import { MetricTimeSeriesBarGraph } from '../../../components/metricTimeSeriesBarGraph/MetricTimeSeriesBarGraph';
import { GradientDefs } from '../../../components/metricMonthlySummaryCard/GradientDefs';
import CoachingGlidePath from '../../../components/coachingActionPlan/coachingGlidePath/CoachingGlidePath';
import { metricValueFormat } from '../../../utils/metricValueFormat';
import './AnalyzeStep.scss';
import { useGetMonthlyMetricHistoryQuery } from '../../../pages/metricdashboard/metricDashboardSlice';
import DescriptionBox from '../../../components/descriptionBox/DescriptionBox';
import { CoachingTabs } from '../../../utils/typeConstants';
import { useCAPAutoSave } from '../hooks/useCAPAutoSave';
import { getExceedsTarget, isTieredScorecard } from '../../../utils/metricAttainment';

interface AnalyzeStepProps {
    coachingActionPlanId?: number;
    onBack?: () => void;
    onContinue?: () => void;
    CAPSessionData?: any;
    metricStartDate?: string | null;
    metricEndDate?: string | null;

}

interface SessionData {
    analysisNotes: string;
    analysisTypes: any;
}

const MemoizedBarGraph = React.memo(({
    selectedDailyElement,
    scorecardVersionId,
    employeeNumber,
    historyData,
}: {
    selectedDailyElement: any;
    scorecardVersionId: number;
    employeeNumber: string;
    historyData: any[];
}) => {
    if (!selectedDailyElement) return null;
    return (
        <div className="expanded-graph-section">
            <MetricTimeSeriesBarGraph
                metricName={selectedDailyElement.label}
                metricId={selectedDailyElement.metric_id}
                scorecardVersionId={scorecardVersionId}
                employeeNumber={employeeNumber}
                target={selectedDailyElement.target}
                metricTypeName={selectedDailyElement.metric_type_name}
                dailyData={selectedDailyElement.dailyMetricSummaryList}
                metricDirectionId={selectedDailyElement.metric_direction_id}
                calculatedDate={selectedDailyElement.calculate_date}
                metricHistoryData={historyData}
                fromMetricDashboard
                metricIndex={0}
                metricOmitted={false}
                currentMonthLabel={'Last 30 Days'}
                historyLabel={'Last 6 Months'}
                exceedsTarget={getExceedsTarget(selectedDailyElement.targets)}
            />
        </div>
    );
});

export const AnalyzeStep: React.FC<AnalyzeStepProps> = ({
    coachingActionPlanId: propPlanId,
    onBack,
    onContinue,
    CAPSessionData,
    metricStartDate,
    metricEndDate,
}) => {
    const [analysisTypes, setAnalysisTypes] = useState<number[]>([]);
    const [analysisNotes, setAnalysisNotes] = useState('');
    const [latestSession, setLatestSession] = useState<any>(null);

    const { data, isLoading } = useGetAnalysisTypesQuery();
    const [saveAnalysisNotes, { isLoading: isSaving }] = useSaveAnalysisNotesMutation();
    const [saveAnalysisTypes] = useSaveAnalysisTypesMutation();
    const [calculateNextBestAction] = useCalculateNextBestActionAssociateMutation();

    const session = CAPSessionData?.sessions?.at(-1);
    const coachingActionPlanId = propPlanId ?? CAPSessionData?.coachingActionPlanId;
    const sessionId: number = session?.coachingActionPlanSessionId;
    const scorecardVersionId = CAPSessionData?.scorecardVersionId ?? 0;
    const scorecardCalculationRuleId = CAPSessionData?.scorecardCalculationRuleId;
    const employeeName = (CAPSessionData?.employeeName ?? '').replace(/\(/, ' (');
    const employeeNumber = CAPSessionData?.employeeNumber ?? '';
    const employeeNumberInt = employeeNumber ? parseInt(employeeNumber, 10) : 0;
    const navigate = useNavigate();
    const location = useLocation();
    const tiered = isTieredScorecard(CAPSessionData?.scorecardCalculationRuleId);

    const selectedMetric = useMemo(() => CAPSessionData ? {
        metricName: CAPSessionData.label,
        metricId: CAPSessionData.metricId,
        target: scorecardCalculationRuleId === 2 ? session?.targets : session?.metricTargetValue ?? CAPSessionData.target,
        currentValue: session?.metricCurrentValue ?? CAPSessionData.metricStartValue,
        metricAttained: session?.sessionCommitmentAttained ?? false,
        metricTypeName: CAPSessionData.metricTypeName,
        metricDirectionId: CAPSessionData.metricDirectionId,
        metricDesc: CAPSessionData.metricDescription,
        metricIndex: 0,
        metricOmitted: false,
        startDate: metricStartDate,
        endDate: metricEndDate,
        min: CAPSessionData.metricMinValue,
        max: CAPSessionData.metricMaxValue,
        metricResultValueAttainmentRank: session?.metricResultValueAttainmentRank,
        metricResultValueAttainmentLevelLabel: session?.metricResultValueAttainmentLevelLabel,

    } : null, [CAPSessionData]);

    const { data: dailyMetricsResponse } = useGetCapDailyMetricSummaryQuery(
        {
            employeeNumber: employeeNumberInt,
            scorecardVersionId,
            scorecardRoleId: 1,
            startDate: metricStartDate,
            endDate: metricEndDate
        },
        { skip: !CAPSessionData || !employeeNumberInt }
    );

    // const updateLatestSession = (field: string, value: any) => {
    //     setLatestSession((prev: any) => ({ ...prev, [field]: value }));
    //     updateField(field as keyof SessionData, value);
    // };

    const selectedDailyElement = useMemo(() => {
        const matches = dailyMetricsResponse?.data?.filter(
            (el: any) => el.label === selectedMetric?.metricName
        ) ?? [];
        if (matches.length === 0) return null;
        return {
            ...matches[0],
            dailyMetricSummaryList: matches
                .flatMap((el: any) => el.dailyMetricSummaryList ?? [])
                .sort((a: any, b: any) =>
                    new Date(b.result_date).getTime() - new Date(a.result_date).getTime()
                ),
        };
    }, [dailyMetricsResponse, selectedMetric?.metricName]);

    const { data: existingTypesData } = useGetSessionAnalysisTypesQuery(sessionId, {
        skip: !sessionId,
    });

    useEffect(() => {
        if (existingTypesData?.data && existingTypesData.data.length > 0) {
            setAnalysisTypes(existingTypesData.data);
        } else if (existingTypesData) {
            setAnalysisTypes([]);
        } else if (CAPSessionData) {
            const s = CAPSessionData?.sessions?.[0];
            const types = s?.analysisTypes ?? s?.analysis_types ?? [];
            if (types.length > 0) setAnalysisTypes(types);
        }
    }, [existingTypesData, CAPSessionData]);

    useEffect(() => {
        sessionStorage.setItem('cap_nav_intent', 'true');
        navigate(location.pathname, {
            replace: true,
            state: { ...location.state, activeStep: 1 }
        });
    }, []);

    const analysisTypeOptions: { label: string; value: number }[] =
        data?.data?.map((type: any) => ({
            label: type.coaching_action_plan_analysis_type_name,
            value: type.coaching_action_plan_analysis_type_id,
        })) || [];

    // ── Fetch monthly history ──
    const { data: metricHistoryResponse } = useGetMonthlyMetricHistoryQuery(
        {
            employeenumber: employeeNumberInt,
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            scorecardVersionId,
        },
        { skip: !selectedMetric || !employeeNumberInt }
    );

    const historyData = useMemo(() =>
        metricHistoryResponse?.data?.filter(
            (el: any) => el.label === selectedMetric?.metricName
        ) ?? []
        , [metricHistoryResponse, selectedMetric?.metricName]);

    const saveAnalysis = async (notes: string, types: any) => {
        await Promise.all([
            notes && saveAnalysisNotes({
                COACHING_ACTION_PLAN_SESSION_ID: sessionId,
                ANALYSIS_NOTES: notes,
            }).unwrap(),
            types && saveAnalysisTypes({
                coachingActionPlanSessionId: sessionId,
                analysisTypeIds: types,
            }).unwrap(),
        ]);
    };

    const handleContinue = async () => {
        if (sessionId) {
            try {
                await saveAnalysis(analysisNotes, analysisTypes);
                await calculateNextBestAction({
                    scorecardVersionId,
                    employeeNumber,
                }).unwrap();
                toast.success('Analysis saved successfully.');
                onContinue?.();
            } catch (error) {
                console.error('Failed to save analysis:', error);
                toast.error('Failed to save analysis. Please try again.');
            }
        } else {
            onContinue?.();
        }
    };

    const { updateField } = useCAPAutoSave<SessionData>({
        data: latestSession,
        onSave: async (updatedData) => {
            if (!sessionId) return;
            try {
                await saveAnalysis(updatedData?.analysisNotes, updatedData?.analysisTypes);
                // toast.success('Auto saved successfully.');
            } catch (error) {
                console.error('Failed to save analysis:', error);
                toast.error('Failed to save analysis. Please try again.');
            }
        },
        idleTimeout: 15000,
    });

    const handleSave = async () => {
        if (!sessionId) return;
        try {
            await saveAnalysis(analysisNotes, analysisTypes);
            // toast.success('Auto saved successfully.');
        } catch (error) {
            console.error('Failed to save analysis:', error);
            toast.error('Failed to save analysis. Please try again.');
        }
    };

    useEffect(() => {
        const session = CAPSessionData?.sessions?.reduce((max: any, s: any) =>
            s.sessionNumber > max.sessionNumber ? s : max
            , CAPSessionData?.sessions?.[0]);
        setLatestSession(session);
    }, [CAPSessionData]);

    return (
        <div className="analyze-step">
            <div className="analyze-step__header">
                <h3 className="analyze-step__title">Analyze: Observations</h3>
                <p className="analyze-step__description">
                    Document your observations after listening to calls, reviewing emails/chats,
                    and examining customer surveys.
                </p>
            </div>

            <div className="analyze-step__analysis-type-section">
                <label htmlFor="analysis-type" className="analyze-step__label">
                    Analysis Type
                </label>
                <MultiSelect
                    id="analysis-type"
                    value={analysisTypes}
                    options={analysisTypeOptions}
                    onChange={(e) => {
                        setAnalysisTypes(e.value);
                        updateField('analysisTypes', e.value);
                    }}
                    placeholder="Select"
                    className="analyze-step__multiselect"
                    disabled={isLoading}
                    selectedItemsLabel={
                        analysisTypes.length > 0
                            ? analysisTypeOptions
                                .filter((opt) => analysisTypes.includes(opt.value))
                                .map((opt) => opt.label)
                                .join(', ')
                            : 'Select'
                    }
                    maxSelectedLabels={0}
                />
            </div>

            {/* Coaching Glide Path */}
            <div className="analyze-step__coaching-glide-path-section">
                <CoachingGlidePath pageType="analyze" CAPSession={CAPSessionData} />
            </div>

            {/* Main Content Grid */}
            {selectedMetric && (
                <div className="analyze-step__grid">
                    {coachingActionPlanId && (
                        <div className="analyze-step__history">
                            <CoachingHistory coachingActionPlanId={coachingActionPlanId}
                                CAPSessionData={CAPSessionData}
                            />
                        </div>
                    )}

                    <div className="analyze-step__metric-card">
                        <GradientDefs />
                        <MetricMonthlySummaryCard
                            targetVal={selectedMetric.target}
                            metricAttained={selectedMetric.metricAttained}
                            metricNameVal={selectedMetric.metricName}
                            metricTypeName={selectedMetric.metricTypeName}
                            metricVal={selectedMetric.currentValue}
                            metricDesc={selectedMetric.metricDesc}
                            metricDirectionId={selectedMetric.metricDirectionId}
                            onCardClick={() => { }}
                            selectedMetric={selectedMetric.metricName}
                            index={selectedMetric.metricIndex}
                            metricOmitted={selectedMetric.metricOmitted}
                            via={CoachingTabs.CoachingActionPlan}
                            CAPCurrentSession={latestSession}
                            metricTypeId={CAPSessionData?.metricTypeId}
                            overrideInfo={
                                latestSession?.metricCurrentValueOverride != null
                                    ? {
                                        label: 'Overridden Data',
                                        value: metricValueFormat(
                                            latestSession.metricCurrentValueOverride,
                                            CAPSessionData.metricTypeName
                                        ),
                                        description: latestSession.metricCurrentValueOverrideReason ?? '',
                                    }
                                    : undefined
                            }
                            onBeforeOverride={handleSave}

                            // metricOmitted={selectedMetric?.metric_omitted}
                            variant={tiered ? 'tiered' : 'legacy'}
                            attainmentRank={selectedMetric?.metricResultValueAttainmentRank}
                            attainmentLevelLabel={selectedMetric?.metricResultValueAttainmentLevelLabel}
                            targets={selectedMetric?.target}
                            min={selectedMetric?.min}
                            max={selectedMetric?.max}
                        />
                    </div>

                    <div className="analyze-step__notes">
                        <DescriptionBox
                            title={`Analysis for ${employeeName}`}
                            responseDescription={latestSession?.analysisNotes}
                            value={latestSession?.analysisNotes}
                            limit={4000}
                            // onChange={(val: string) => updateField('analysisNotes', val)}
                            onChange={(val: string) => {
                                setAnalysisNotes(val);
                                updateField('analysisNotes', val);
                            }}
                            via={CoachingTabs.CoachingActionPlan}
                        />
                    </div>

                    <div className="analyze-step__graph">
                        <MemoizedBarGraph
                            selectedDailyElement={selectedDailyElement}
                            scorecardVersionId={scorecardVersionId}
                            employeeNumber={employeeNumber}
                            historyData={historyData}
                        />
                    </div>
                </div>
            )}

            {!selectedMetric && (
                <div className="analyze-step__grid">
                    {coachingActionPlanId && (
                        <div className="analyze-step__history">
                            <CoachingHistory coachingActionPlanId={coachingActionPlanId} />
                        </div>
                    )}
                    <div style={{ gridColumn: '2 / -1', gridRow: '1 / -1' }}>
                        <div className="card p-4 text-center">
                            <p className="text-muted mb-0">No metric selected. Please go back to Prepare step and select a metric.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Back / Continue Buttons */}
            <div className="analyze-step__footer">
                <Button
                    label="Back"
                    icon="pi pi-arrow-left"
                    className="btn-secondary"
                    onClick={() => onBack?.()}
                />
                <Button
                    label="Continue"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    className="btn-primary"
                    onClick={handleContinue}
                    loading={isSaving}
                    disabled={isSaving}
                />
            </div>
        </div>
    );
};